/**
 * Red banner shown across the operator app when the current facility has a gate
 * provider configured but it is NOT verified-reachable (gate_verified_at null).
 * This is the "your gate saved but will never open" warning — the thing that
 * would otherwise strand an operator's first tenant at the gate. Self-hides when
 * no provider is configured or the gate last tested OK.
 */
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { useCurrentFacilitySlug, resolveCurrentFacility } from "./currentFacility";
import { Icon } from "./Icon";

export function GateHealthBanner() {
  const facility = useQuery({
    queryKey: ["facilities", "current", useCurrentFacilitySlug() ?? ""],
    queryFn: async () => await resolveCurrentFacility(),
    retry: false,
  });
  const f = facility.data;
  if (!f) return null;

  const configured = Boolean(
    f.gate_provider ||
      f.pti_falcon_base_url ||
      f.securguard_base_url ||
      f.insomniac_base_url ||
      f.noke_base_url ||
      f.davincilock_base_url ||
      f.intelligate_base_url ||
      f.janus_base_url,
  );
  // Verified-reachable → nothing to warn about. Not configured → the gate page
  // already says "not configured"; no alarm needed here.
  if (!configured || f.gate_verified_at) return null;

  return (
    <div className="flex items-center gap-3 px-5 lg:px-7 py-2 bg-alert text-white text-[13px]">
      <Icon name="warning" size={14} className="shrink-0" />
      <span className="flex-1 font-medium">
        Your gate isn’t connected — tenant access codes won’t open it.
        {f.gate_health_detail ? ` ${f.gate_health_detail}.` : ""} Fix it before your next move-in.
      </span>
      <Link
        to="/app/settings/gate"
        className="font-semibold underline underline-offset-2 whitespace-nowrap"
      >
        Settings → Gate
      </Link>
    </div>
  );
}
